import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { FinancialServicesUIProvider, TransactionCategory } from '@shipstation/financial-services-ui-components';
import { cloneDeep } from 'lodash-es';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useWizard } from 'ui-components/components/wizard/context';
import { noop } from 'ui-components/default-props/noop';

import { BlackBoxSection } from './black-box-section';
import { WalletPaymentDetails } from './wallet-payment-details';
import { FIELD_NAMES } from '../../../../../address/address-fields/constants';
import { useFsuiContext } from '../../../../../financial-services-ui/fsui-context-provider';
import { pollForNewPaymentMethod } from '../../../../../financial-services-ui/poll-for-new-payment-method';
import fsuiStyles from '../../../../../financial-services-ui/fsui-styles.module.less';
import { fsuiToSsuiAddressFieldMap, mapWalletFormBillingAddressToFsuiBillingInfo } from '../../../../../financial-services-ui/mapping';
import { useHumanScript } from '../../../../../hooks/use-human-script';
import { useLocalStorage, WALLET_REGISTRATION_DATA } from '../../../../../hooks/use-local-storage';

import { useLocale } from '../../../../../intl/hooks';
import { usePaymentAndSubscriptionMegaContext } from '../../../../../pages/settings/payment-and-subscription/context';
import { updateCompanyNameAndPhone } from '../../../../../pages/setup/api/setup.api';
import { getSellerLight } from '../../../../../store/seller/seller.actions';
import { useRequirementsChecklist } from '../../../../hooks/use-requirements-checklist';
import { messages } from '../../../../messages';
import { FORM_REQUIREMENTS, PAYMENT_AND_LOCATION_FORMS } from '../../../../utilities/constants';
import { BUSINESS, PERSONAL } from '../../../constants';
import { WalletRegistrationErrorBanner } from '../../components/wallet-registration-error-banner';
import { useWalletRegistrationErrorContext } from '../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../contexts/wallet-registration-form-context';
import { useWalletRegistrationModalContext } from '../../contexts/wallet-registration-modal-context';
import commonStyles from '../../style.module.css';

export const AdyenSimplePickerBillingStep = () => {
  // state
  const dispatch = useDispatch();
  const { locale } = useLocale();
  const { phone, companyName, homeCountry: sellerHomeCountry } = useSelector((state) => state)?.seller ?? {};

  const { reloadData, paymentMethodDetails } = usePaymentAndSubscriptionMegaContext();

  const { walletToRegister: wallet, requiredForms, isPickupSameAsBilling } = useWalletRegistrationModalContext();
  const { errors, setErrors } = useWalletRegistrationErrorContext();
  const { walletForm, setIsPaymentComplete, setAdyenSubmitError, hasSubmittedPayment, setHasSubmittedPayment, updateWalletForm } =
    useWalletRegistrationFormContext();
  // NOTE: this ref is _critical_ to keeping to the most recent data, since handleBeforeRedirect does _not_ seem to update on change inside FSUI like I would expect.
  // it seemed to work for a while without a ref, but after some refactors it stopped. I suspect this is because I was accidentally re-rendering the entire component tree by accident.
  const walletFormRef = useRef(walletForm);
  const [isPolling, setIsPolling] = useState(false);
  const isPaymentProcessingRef = useRef(false);
  const [currentRegisterData, setValue] = useLocalStorage(WALLET_REGISTRATION_DATA);

  const { stepForward, setCanProgress } = useWizard();

  const currentRequiredForms = useMemo(() => {
    const defaultRequiredForms = requiredForms.filter((form) => PAYMENT_AND_LOCATION_FORMS.includes(form));
    return defaultRequiredForms.filter((form) => form !== FORM_REQUIREMENTS.NICKNAME);
  }, [requiredForms]);
  const { checklistCompleted, updateRequirementStatus } = useRequirementsChecklist(currentRequiredForms);

  // BlackBoxSection now handles this logic internally

  // FSUI
  const { onPaymentCompleted, onError, apiConfig, isPreloadingSession, preloadedSession, state } = useFsuiContext();
  const [submitFunction, setSubmitFunction] = useState(null);
  const setFsuiBillingInfoRef = useRef(null);

  // callbacks
  const getShouldUpdateCompanyOrPhone = useCallback(
    (billingInfo) => {
      return companyName !== (billingInfo[FIELD_NAMES.COMPANY] ?? '') || phone !== billingInfo[FIELD_NAMES.PHONE];
    },
    [companyName, phone],
  );

  const handleSuccess = useCallback(
    (shouldPoll = true) => {
      isPaymentProcessingRef.current = true;
      setErrors();

      const billingInfo = walletFormRef?.current?.BillingAddress ?? {};
      const shouldUpdateCompanyOrPhone = getShouldUpdateCompanyOrPhone(billingInfo);
      if (shouldUpdateCompanyOrPhone) {
        updateCompanyNameAndPhone(billingInfo?.company, billingInfo?.phone)
          .then(() => {
            dispatch(getSellerLight());
          })
          .catch(() => {
            // eat error
          });
      }

      if (!shouldPoll) {
        setIsPaymentComplete(true);
        stepForward();
        return;
      }

      setIsPolling(true);
      pollForNewPaymentMethod({
        reloadData,
        previousCount: paymentMethodDetails?.length ?? 0,
      }).finally(() => {
        setIsPaymentComplete(true);
        setIsPolling(false);
        stepForward();
      });
    },
    [setIsPaymentComplete, setErrors, stepForward, getShouldUpdateCompanyOrPhone, dispatch, reloadData, paymentMethodDetails],
  );

  const handleError = useCallback(
    (step, paymentMethod, error) => {
      setAdyenSubmitError(true);
      setHasSubmittedPayment(false);
      onError(error);
    },
    [setAdyenSubmitError, setHasSubmittedPayment, onError],
  );

  const setFsuiSubmitPaymentFunction = useCallback((submit) => {
    setSubmitFunction(() => submit);
  }, []);

  const handleSubmit = useCallback(() => {
    if (submitFunction) {
      submitFunction();
      setHasSubmittedPayment(false);
    }
  }, [submitFunction, setHasSubmittedPayment]);

  const handlePaymentCompleted = useCallback(
    (result) => {
      onPaymentCompleted(result, handleSuccess, handleError);
    },
    [onPaymentCompleted, handleSuccess, handleError],
  );

  const handleBeforeRedirect = useCallback(async () => {
    // this is where we will save stuff to localstorage for post-redirect handling
    if (!preloadedSession) {
      return;
    }

    const billingInfo = walletFormRef?.current?.BillingAddress ?? {};
    const shouldUpdateCompanyOrPhone = getShouldUpdateCompanyOrPhone(billingInfo);
    if (shouldUpdateCompanyOrPhone) {
      try {
        await updateCompanyNameAndPhone(billingInfo?.company, billingInfo?.phone);
      } catch {
        // eat error
      }
    }

    const fakedWalletForm = cloneDeep(walletFormRef.current);
    fakedWalletForm.MetaData = { ...fakedWalletForm.MetaData };
    fakedWalletForm.MetaData.additionalMetadata = {
      ...fakedWalletForm.MetaData?.additionalMetadata,
      sessionId: state?.auctanePaySessionId,
    };
    setValue({
      ...currentRegisterData,
      [preloadedSession.sessionId]: {
        walletForm: fakedWalletForm,
        preloadedSession,
      },
    });
  }, [currentRegisterData, getShouldUpdateCompanyOrPhone, preloadedSession, setValue, state?.auctanePaySessionId]);

  const creditCardConfig = useMemo(
    () => ({
      currencyCode: wallet?.currencyCode,
      inputBorderRadius: '4px',
      inputHeight: '32px',
      locale,
      onError: handleError,
      onPaymentCompleted: handlePaymentCompleted,
      returnUrl: window.location.origin,
      submitPayment: setFsuiSubmitPaymentFunction,
      transactionCategory: TransactionCategory.AddFunds,
      displayMode: 'compact',
    }),
    [handleError, handlePaymentCompleted, locale, setFsuiSubmitPaymentFunction, wallet?.currencyCode],
  );

  const payByBankUsConfig = useMemo(
    () => ({
      beforeRedirect: handleBeforeRedirect,
      currencyCode: wallet?.currencyCode,
      locale,
      session: preloadedSession,
      onError: handleError,
      onPaymentCompleted: noop, // unused in PBB directly because of redirect
      returnUrl: window.location.href,
      submitPayment: setFsuiSubmitPaymentFunction,
      transactionCategory: TransactionCategory.AddFunds,
    }),
    [handleBeforeRedirect, handleError, locale, preloadedSession, setFsuiSubmitPaymentFunction, wallet?.currencyCode],
  );

  const handleOnBillingInfoUpdate = useCallback(
    (billingInfo, isFsuiInfoValid) => {
      if (isPaymentProcessingRef.current) {
        return;
      }

      const accountType = billingInfo?.company ? BUSINESS : PERSONAL;
      // updateWalletForm updates values for fields in the form state off in the void
      // updateRequirementStatus marks if that a form type is passing validation or not
      // so, on update, we update the key array with found values from FSUI billingInfo

      // accountType handling
      updateWalletForm([FORM_REQUIREMENTS.ACCOUNT_TYPE], accountType);

      // handle validation
      updateRequirementStatus(FORM_REQUIREMENTS.BILLING_ADDRESS, isFsuiInfoValid);
      if (isPickupSameAsBilling) {
        updateRequirementStatus(FORM_REQUIREMENTS.PICKUP_ADDRESS, isFsuiInfoValid);
      }

      if (!billingInfo) {
        return;
      }

      // handle setting form values into the SSUI form
      const fsuiKeys = Object.keys(billingInfo);
      fsuiKeys.forEach((key) => {
        const ssuiKey = fsuiToSsuiAddressFieldMap[key];
        if (ssuiKey) {
          updateWalletForm([FORM_REQUIREMENTS.BILLING_ADDRESS, ssuiKey], billingInfo[key]);
          if (isPickupSameAsBilling) {
            updateWalletForm([FORM_REQUIREMENTS.PICKUP_ADDRESS, ssuiKey], billingInfo[key]);
          }
        }
      });
    },
    [isPickupSameAsBilling, updateWalletForm, updateRequirementStatus],
  );

  const handleExistingPaymentMethodSelected = useCallback(() => {
    // as we have selected an existing payment method, we can reset the submit function
    setSubmitFunction(null);
  }, []);

  // effects
  useEffect(() => {
    setCanProgress(checklistCompleted);
  }, [setCanProgress, errors, checklistCompleted]);

  useEffect(() => {
    if (!hasSubmittedPayment) {
      return;
    }

    // because of AddressValidationModal, we want to double-check our billinginfo before submitting
    const walletFormBillingAddress = walletForm.BillingAddress;
    const newFsuiBillingInfo = mapWalletFormBillingAddressToFsuiBillingInfo(walletFormBillingAddress);
    setFsuiBillingInfoRef.current?.(newFsuiBillingInfo, true);

    // hack to allow the FSUI billing context to update before submitting
    setTimeout(() => {
      if (submitFunction) {
        handleSubmit();
      } else {
        // At this point, we have no submit function, so we have selected an existing payment method
        handleSuccess(false);
        // once we have the payment method selected, we can reset the hasSubmittedPayment state
        // simulating that we have done the Adyen processing
        setHasSubmittedPayment(false);
      }
    }, 1);
  }, [hasSubmittedPayment, handleSubmit, submitFunction, checklistCompleted, setHasSubmittedPayment, handleSuccess, walletForm]);

  useEffect(() => {
    walletFormRef.current = walletForm;
  }, [walletForm]);

  useHumanScript();

  // render conditions
  return (
    <div className={commonStyles['step-content']}>
      <WalletRegistrationErrorBanner />

      <BlackBoxSection updateRequirementStatus={updateRequirementStatus} />

      <div className={commonStyles.header}>
        <FormattedMessage {...messages.paymentTabHeader} />
      </div>
      <div className={fsuiStyles['fsui-wrapper']}>
        {isPreloadingSession || isPolling || !preloadedSession?.eligiblePaymentMethodTypes?.length ? (
          <LoadingIndicator className={fsuiStyles.loader} />
        ) : (
          <FinancialServicesUIProvider
            apiConfig={apiConfig}
            billingInfo={undefined} // at this time, we never pre-populate this. Open to doing so in the future. Might come into play in redirect/error flows.
            initialSession={preloadedSession}
            locale={locale}
            mantineTheme={{
              colors: {
                indigo: [
                  '#F7F7FF', // --Indigo-050
                  '#EEF0FF', // --Indigo-100
                  '#DADEFF', // --Indigo-200
                  '#B0B8FF', // --Indigo-300
                  '#8791FF', // --Indigo-400
                  '#636EFF', // --Indigo-500
                  '#4D55E6', // --Indigo-600
                  '#3D3CC2', // --Indigo-700
                  '#2F2699', // --Indigo-800
                  '#221469', // --Indigo-900
                  '#11003A', // --Indigo-950
                ],
              },
            }}
            onBillingInfoUpdate={handleOnBillingInfoUpdate}
            useMantine
            userCountryCode={sellerHomeCountry}
          >
            <WalletPaymentDetails
              creditCardConfig={creditCardConfig}
              onExistingPaymentMethodSelected={handleExistingPaymentMethodSelected} // no-op for single payment method
              payByBankUsConfig={payByBankUsConfig}
              setFsuiBillingInfoRef={setFsuiBillingInfoRef}
              updateRequirementStatus={updateRequirementStatus}
            />
          </FinancialServicesUIProvider>
        )}
      </div>
    </div>
  );
};
